import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { func, oneOf, shape, string } from 'prop-types';
import { useExperienceContext } from '../../hooks/useExperienceContext';

const LensCornerBorder = ({ xEdge, yEdge }) => {
  const { smModifierEnabled } = useExperienceContext();

  return (
    // eslint-disable-next-line tailwindcss/no-custom-classname
    <div className={`sui-absolute sui-${yEdge}-0 sui-${xEdge}-0`}>
      <div
        className={
          /** non-mobile responsive design styles */
          classNames({
            'sm:sui-h-6 sm:sui-w-6': smModifierEnabled,
          },
          /** mobile-first design styles */
          'sui-h-3',
          'sui-w-3',
          /** universal design styles */
          'sui-relative',
          'sui-overflow-hidden',
          )
        }
      >
        <div
          className={
            classNames({
              'sm:sui-h-12 sm:sui-w-12 sm:sui-border-[5px] sm:sui-rounded-[12px]': smModifierEnabled,
            },
            /** mobile-first design styles */
            'sui-h-7',
            'sui-w-7',
            'sui-border-4',
            'sui-rounded-lg',
            /** universal design styles */
            'sui-border-solid',
            'sui-border-[white]',
            'sui-absolute',
            `sui-${yEdge}-0`,
            `sui-${xEdge}-0`,
            )
          }
        />
      </div>
    </div>
  );
};

LensCornerBorder.propTypes = {
  xEdge: oneOf(['left', 'right']).isRequired,
  yEdge: oneOf(['top', 'bottom']).isRequired,
};

// TODO: The current implementation uses CSS styling to build the UI and behavior. In a future sprint, we might consider
// experimenting with a different approach to build this using a <canvas> element. One potential issue with the current
// implementation is the background image may not get sized correctly when the user adjusts the zoom level or viewport.
const HotspotLensOverlay = ({
  boundingBox,
  imageRef,
  imageUrl,
}) => {
  const [imageSize, setImageSize] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const { xMin, xMax, yMin, yMax } = boundingBox;
  const { height, width } = imageSize;

  const updateImageSize = (_event, ref) => {
    /* istanbul ignore else */
    if (ref?.current) {
      const imageBoundingClientRect = ref.current.getBoundingClientRect();
      setImageSize({ width: imageBoundingClientRect.width, height: imageBoundingClientRect.height });
    }
  };

  useEffect(() => {
    updateImageSize(null, imageRef);
  }, [imageRef]);

  /* istanbul ignore next */
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', (event) => updateImageSize(event, imageRef));
    }
    return () => window.removeEventListener('resize', (event) => updateImageSize(event, imageRef));
  }, [imageRef]);

  useEffect(() => {
    setIsLoading(!height);
  }, [height]);

  useEffect(() => {
    setIsLoading(!width);
  }, [width]);

  return (
    <div
      aria-label="Lens Overlay"
      className={classNames('sui-absolute', { 'sui-invisible': isLoading })}
      style={{
        top: `${yMin * 100}%`,
        right: `${(1 - xMax) * 100}%`,
        bottom: `${(1 - yMax) * 100}%`,
        left: `${xMin * 100}%`,
      }}
    >
      <div
        aria-label="Lens Background Image"
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        className="sui-h-[calc(100%-4px)] sui-m-2px sui-rounded-lg sui-overflow-hidden"
        style={{
          background: `url(${imageUrl})`,
          backgroundSize: `${width}px ${height}px`,
          backgroundPosition: `-${width * Number(xMin) + 2}px -${height * Number(yMin) + 2}px`,
        }}
      />
      <LensCornerBorder yEdge="top" xEdge="left" />
      <LensCornerBorder yEdge="top" xEdge="right" />
      <LensCornerBorder yEdge="bottom" xEdge="right" />
      <LensCornerBorder yEdge="bottom" xEdge="left" />
    </div>
  );
};

HotspotLensOverlay.displayName = 'HotspotLensOverlay';

HotspotLensOverlay.propTypes = {
  boundingBox: shape({
    xMin: string,
    xMax: string,
    yMin: string,
    yMax: string,
  }).isRequired,
  imageRef: shape({ current: shape({ getBoundingClientRect: func }) }),
  imageUrl: string,
};

HotspotLensOverlay.defaultProps = {
  imageRef: null,
  imageUrl: '',
};

export { HotspotLensOverlay };
